import {
  AudienceDetailPage,
  AudiencesPage,
  DocInspectorPage,
  DocsArticlesPage,
  DocsGapsPage,
  DocsTeamPage,
  DocsLayout,
  DocsOverviewPage,
  DocsSettingsPage,
  DocsSourcesView,
  LegacySourceRedirect,
  UserDetailPage,
  UsersInsightsPage,
  UsersLayout,
  UsersRosterPage,
  UsersSettingsPage,
} from './lazy-pages';

import type { PageOrRedirectRouteEntry } from './route-table.types';

/**
 * The two ROUTED-IA hubs, and the sibling routes whose correctness depends on
 * how they are matched.
 *
 * Split out of `route-table.in-shell.ts` for the ~300-line ceiling
 * (`.claude/rules/architecture.md`) along a real seam rather than a per-feature
 * one — the same kind of seam the redirects file next door uses. Those entries
 * belong together because they render nothing and their order is immaterial;
 * these belong together because they share a routing PROPERTY that the rest of
 * the table does not have, and that property is load-bearing:
 *
 *   - a LAYOUT entry with `children`, which stays mounted while the outlet
 *     swaps, so the hub's shared read happens once and its sub-navigation does
 *     not remount on every click;
 *   - one or more full pages at a DEEPER path that sit outside the layout
 *     (`/users/audiences/:id`, `/docs/sources/:sourceId`) — a detail view with
 *     its own back link is a page, not a tab;
 *   - a DYNAMIC one-segment sibling (`/users/:sub`, `/docs/:sourceId`) which
 *     must never swallow a static sibling view.
 *
 * **That last line is the reason this file exists rather than two blocks 165
 * lines apart.** React Router ranks a static segment above a dynamic one, so
 * `/docs/articles` matches the layout's `articles` child and not
 * `/docs/:sourceId`. That is a library behaviour this repo RELIES on and does
 * not own, and if it ever changed the failure would be a whole view becoming
 * unreachable while every unit test of that view stayed green. It is asserted
 * at the router (`routes.test.tsx`) rather than trusted — and stating the
 * invariant once, here, beats restating it wherever the next hub lands.
 *
 * Declaration ORDER within the table is immaterial to matching (the parent's
 * own header says so), so grouping these is behaviour-neutral; `routes.test.tsx`
 * and `route-table-coverage.test.tsx` both exercise the composed table.
 */
export const inShellHubEntries: readonly PageOrRedirectRouteEntry[] = [
  // DE-301 § 7.2 — the documentation hub: an Overview INDEX plus four job-named
  // views, on REAL routes under one layout. Four of them are shareable objects,
  // an article needs a URL, and the overflow sweep keys on path — so views
  // behind a `?tab=` param would never be swept. `DocsLayout` forwards every
  // legacy `?tab=` bookmark before rendering.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/docs',
    component: DocsLayout,
    children: [
      { component: DocsOverviewPage },
      { path: 'articles', component: DocsArticlesPage },
      // § 7.2 — an article needs a URL. The SAME view with one open, so the
      // queue does not remount underneath the reader and Back means "the
      // article I was just reading".
      { path: 'articles/:articleId', component: DocsArticlesPage },
      { path: 'gaps', component: DocsGapsPage },
      // DE-504 — the team knowledge base. Same shape as `articles` above and
      // for the same reason: the thing a CS manager does with this is send
      // somebody a link, so an open article is a route rather than a modal,
      // and the list does not remount underneath the reader.
      { path: 'team', component: DocsTeamPage },
      { path: 'team/:articleId', component: DocsTeamPage },
      { path: 'sources', component: DocsSourcesView },
      { path: 'settings', component: DocsSettingsPage },
    ],
  },
  // The per-source inspector is a FULL page with its own back link, outside the
  // layout — the `/users/audiences/:id` shape below. Its `sources` segment is
  // static, so it can never shadow a sibling view.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/docs/sources/:sourceId',
    component: DocInspectorPage,
  },
  // Every saved inspector deep link is of the OLD one-segment shape. A component
  // rather than a redirect entry, because the table's `to` is a static string
  // and this has to carry the param (and the chunk-jump query) through. Declared
  // LAST among the `/docs` entries so the intent is readable, though it is the
  // ranking described in this file's header that actually keeps it from
  // shadowing a sibling view.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/docs/:sourceId',
    component: LegacySourceRedirect,
  },
  // UGL-105 A0 — the routed Users surface. `UsersLayout` owns the shared status
  // read + sub-navigation and stays mounted across a sub-navigation; the four
  // children replace the pre-split `?tab=` tabs (legacy `?tab=` bookmarks are
  // redirected from inside the layout).
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/users',
    component: UsersLayout,
    children: [
      { component: UsersRosterPage },
      { path: 'audiences', component: AudiencesPage },
      { path: 'insights', component: UsersInsightsPage },
      { path: 'settings', component: UsersSettingsPage },
    ],
  },
  // UGL-104 — audience detail; a full page with its own back link. Its static
  // `audiences` segment wins over the dynamic sub param below.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/users/audiences/:id',
    component: AudienceDetailPage,
  },
  { kind: 'page', placement: 'in-shell', path: '/users/:sub', component: UserDetailPage },
];
